import * as React from "react";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RunWithChoice, keyCanRun, type RunWith } from "@/components/epic-requests/run-with-choice";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { useAiSettings } from "@/hooks/queries/use-ai-settings";
import {
  machineMinimumVersion,
  machineReadiness,
  machineServicesRemote,
  type Machine,
} from "@/lib/api/mailbox";
import type { FixJobTarget } from "@/lib/api/pull-requests";

/** Why a paired computer can't take a fix run for a repository right now. */
export type MachineUnavailableReason =
  | "offline"
  | "outdated"
  | "no-runtime"
  | "no-repo"
  | "repo-not-served";

/**
 * Why `machine` can't run work on `targetRemote`, or null when it can. Uses
 * the same readiness data as the eligibility filter (server readiness, CLI
 * version vs. minimum, serviced remotes), so null ⇔ the machine is eligible.
 * A machine that isn't ready reports that first — the repo check only matters
 * once the computer can work at all.
 */
export function machineUnavailableReason(
  machine: Machine,
  targetRemote: string,
  now: number = Date.now()
): MachineUnavailableReason | null {
  const readiness = machineReadiness(machine, now);
  if (readiness === "offline") return "offline";
  if (readiness === "outdated") return "outdated";
  if (readiness === "runtime-not-found") return "no-runtime";
  if (machineServicesRemote(machine, targetRemote)) return null;
  return (machine.servicedRemotes ?? []).length === 0 ? "no-repo" : "repo-not-served";
}

/** Closest-to-ready first: a quick update beats installing a runtime, cloning, or waking a computer. */
const REASON_RANK: Record<MachineUnavailableReason, number> = {
  outdated: 0,
  "no-runtime": 1,
  "repo-not-served": 2,
  "no-repo": 3,
  offline: 4,
};

/** The unavailable computer closest to being ready, with its reason; null if any computer qualifies or none exist. */
export function bestUnavailableMachine(
  machines: Machine[],
  targetRemote: string,
  now: number = Date.now()
): { machine: Machine; reason: MachineUnavailableReason } | null {
  let best: { machine: Machine; reason: MachineUnavailableReason } | null = null;
  for (const machine of machines) {
    const reason = machineUnavailableReason(machine, targetRemote, now);
    if (reason === null) return null;
    if (!best || REASON_RANK[reason] < REASON_RANK[best.reason]) best = { machine, reason };
  }
  return best;
}

function unavailableMessage(
  machine: Machine,
  reason: MachineUnavailableReason,
  repoFullName: string
): string {
  const name = machine.name;
  switch (reason) {
    case "offline":
      return `${name} is offline. Start CompBox on it, then try again.`;
    case "outdated": {
      const min = machineMinimumVersion(machine);
      return `${name} needs a CompBox update${min ? ` (version ${min} or later)` : ""} before it can take this work.`;
    }
    case "no-runtime":
      return `${name} is connected, but Claude Code wasn't found on it. Install Claude Code, then try again.`;
    case "no-repo":
      return `${name} isn't set up with any repositories yet. Clone ${repoFullName} on it and run CompBox from there.`;
    case "repo-not-served":
      return `${name} doesn't have ${repoFullName}. Clone it on that computer and run CompBox from there.`;
  }
}

/**
 * Where a fix run (send back / resolve conflicts) goes: a paired computer that
 * services the repository (on the computer's login or the person's API key),
 * or — on a key with no paired computer — a CompBox cloud workspace.
 * Reports null while no valid target is chosen.
 */
export function FixTargetPicker({
  repoFullName,
  active,
  onChange,
}: {
  repoFullName: string;
  /** Re-selects a default computer whenever this turns true (dialog opened). */
  active: boolean;
  onChange: (target: FixJobTarget | null) => void;
}) {
  const { data: machines } = useMachines();
  const { data: aiSettings } = useAiSettings();
  const [runWithChoice, setRunWithChoice] = React.useState<RunWith | null>(null);
  const [selectedMachineId, setSelectedMachineId] = React.useState<string | null>(null);

  const hasComputer = (machines?.length ?? 0) > 0;
  const keyReady = keyCanRun(aiSettings, "execution", hasComputer);
  const runWith: RunWith = runWithChoice ?? (!hasComputer && keyReady ? "key" : "computer");
  // No repo known → don't filter by repo (a bare "https://github.com/" matches nothing).
  const remote = repoFullName ? `https://github.com/${repoFullName}` : "";

  const eligible = React.useMemo(
    () =>
      (machines ?? []).filter(
        (m) => machineReadiness(m) === "ready" && machineServicesRemote(m, remote)
      ),
    [machines, remote]
  );

  React.useEffect(() => {
    if (!active) return;
    setSelectedMachineId((current) =>
      current && eligible.some((m) => m.id === current) ? current : (eligible[0]?.id ?? null)
    );
  }, [active, eligible]);

  const toCloud = runWith === "key" && !hasComputer;
  const unavailable = React.useMemo(
    () => (eligible.length === 0 ? bestUnavailableMachine(machines ?? [], remote) : null),
    [eligible, machines, remote]
  );

  React.useEffect(() => {
    if (toCloud) {
      onChange(keyReady ? { runsIn: "cloud" } : null);
    } else {
      onChange(
        selectedMachineId
          ? { runsIn: "machine", machineId: selectedMachineId, useAiKey: runWith === "key" }
          : null
      );
    }
  }, [toCloud, keyReady, selectedMachineId, runWith, onChange]);

  return (
    <div className="space-y-3">
      <RunWithChoice
        settings={aiSettings}
        kind="execution"
        hasComputer={hasComputer}
        value={runWith}
        onChange={setRunWithChoice}
      />
      {!toCloud &&
        (eligible.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="fix-target-unavailable">
            {unavailable
              ? unavailableMessage(unavailable.machine, unavailable.reason, repoFullName)
              : hasComputer
                ? "None of your computers is ready to work on this repository right now."
                : "Pair a computer to run the agent, or use your API key."}{" "}
            {hasComputer && (
              <Link to="/settings#computers" className="underline">
                Computer settings
              </Link>
            )}
          </p>
        ) : eligible.length > 1 ? (
          <div className="space-y-1.5">
            <Label>Computer</Label>
            <Select value={selectedMachineId ?? ""} onValueChange={setSelectedMachineId}>
              <SelectTrigger aria-label="Computer">
                <SelectValue placeholder="Choose a computer" />
              </SelectTrigger>
              <SelectContent>
                {eligible.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null)}
    </div>
  );
}
